import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail } from "lucide-react";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Privacy Policy
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Privacy Policy</h1>
              <p className="text-slate-500">Last updated: January 2025</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600 mb-8">
              KiddiPlay Club ("we", "our", "us") is committed to protecting your family's privacy. This policy explains how we collect, use, and safeguard your personal information in compliance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Database className="w-5 h-5 text-purple-500" />
                Information We Collect
              </h2>
              <p className="text-slate-600 mb-4">We collect information you provide directly:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
                <li><strong>Family Profile:</strong> Parent/guardian names, children's first names and ages, interests, dietary requirements, accessibility needs</li>
                <li><strong>Location Data:</strong> City/town and postcode (for local matching only - we do not track real-time location except during Safety Check-ins)</li>
                <li><strong>Communication Data:</strong> Messages sent through our platform (monitored for safety)</li>
                <li><strong>Photos:</strong> Profile pictures you choose to upload (optional)</li>
              </ul>
              
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <h3 className="font-semibold text-amber-800 mb-2">Safety Check-In Location Sharing</h3>
                <p className="text-amber-700 text-sm">
                  When you agree to a meetup with another family, both parties can opt-in to our Safety Check-In feature. 
                  This allows you to share your location at the meetup venue to confirm arrival. Location data during check-ins 
                  is only visible to the other family in that specific meetup and is not stored beyond the meetup duration. 
                  We encourage all families to use Safety Check-Ins for peace of mind.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Eye className="w-5 h-5 text-purple-500" />
                How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>To match you with compatible families in your area</li>
                <li>To enable communication between matched families</li>
                <li>To provide features like the Playdate Planner and Share a Sitter</li>
                <li>To monitor for safeguarding concerns and enforce community guidelines</li>
                <li>To improve our services and develop new features</li>
                <li>To send important service updates (you can opt out of marketing)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Lock className="w-5 h-5 text-purple-500" />
                Data Security
              </h2>
              <p className="text-slate-600 mb-4">We take your family's security seriously:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>All data is encrypted in transit (HTTPS) and at rest</li>
                <li>Passwords are securely hashed - we cannot see your password</li>
                <li>We use secure cloud infrastructure with regular security audits</li>
                <li>Staff access to personal data is strictly limited and logged</li>
                <li>We automatically filter personal contact information from messages</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <UserCheck className="w-5 h-5 text-purple-500" />
                Your Rights Under UK GDPR
              </h2>
              <p className="text-slate-600 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Access:</strong> Request a copy of all data we hold about you</li>
                <li><strong>Rectification:</strong> Correct any inaccurate information</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Object:</strong> Object to certain processing of your data</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Children's Privacy</h2>
              <p className="text-slate-600">
                We collect minimal information about children (first name, age, interests) solely to facilitate appropriate playdate matching. We never collect children's contact details or allow direct communication with children. All accounts must be held by adults (18+) who are parents or legal guardians.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Data Retention</h2>
              <p className="text-slate-600">
                We retain your data while your account is active. If you delete your account, we will delete your personal data within 30 days, except where we are legally required to retain it (e.g., for safeguarding records).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Cookies</h2>
              <p className="text-slate-600">
                We use essential cookies to keep you logged in and remember your preferences. We do not use third-party advertising cookies. Analytics cookies (if used) are anonymised.
              </p>
            </section>

            <section className="mb-8 p-6 bg-purple-50 rounded-xl">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Mail className="w-5 h-5 text-purple-500" />
                Contact Us
              </h2>
              <p className="text-slate-600 mb-2">
                For any privacy-related questions or to exercise your rights:
              </p>
              <p className="text-slate-700">
                <strong>Email:</strong> privacy@kiddiplayclub.com<br />
                <strong>Data Protection Officer:</strong> team@kiddiplayclub.com
              </p>
              <p className="text-slate-600 mt-4">
                You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" className="text-purple-600 underline">ico.org.uk</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
